import Radio from "@/components/Radio";
import { CompensationProfileFormInput } from "../types";
import { Controller, UseFormReturn } from "react-hook-form";
import TextField from "@/components/TextField";
import { NumberFormatBase } from "react-number-format";
import { currencyFormatterNoDecimals } from "@/lib/currencyHelpers";
import { getCompensationRateLabel } from "@/lib/compensationRateHelpers";
import Divider from "@/components/Divider";

const COMPENSATION_SCALES = [
  { label: "Salary", value: "Salary" },
  { label: "Weekly", value: "Weekly" },
  { label: "Hourly", value: "Hourly" }
];

export function CompensationSummaryStep({
  form,
  includeInitialTotalCompensation = false
}: {
  form: UseFormReturn<CompensationProfileFormInput>;
  includeInitialTotalCompensation?: boolean;
}) {
  const rateLabel = getCompensationRateLabel(form.watch("basePayType"));

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter the high level summary of compensation.</div>
      <div className="col-span-full">
        {includeInitialTotalCompensation && (
          <div className="col-span-full mb-6">
            <p className="text-title-medium mb-2">Initial Total Compensation</p>
            <p className="text-body-medium text-sys-brand-on-surface mb-2">
              Total compensation (including pay, bonuses, lessons, etc.) prior to the compensation you are currently
              reporting. If the total compensation has not changed recently, this should match "Total Compensation",
              below.
            </p>
            <Controller
              control={form.control}
              name="initialTotalCompensation"
              render={({ field }) => (
                <NumberFormatBase
                  disabled={false}
                  value={field.value}
                  name="initialTotalCompensation"
                  format={currencyFormatterNoDecimals}
                  className="w-full"
                  customInput={TextField}
                  error={!!form.formState.errors.initialTotalCompensation}
                  errorText={form.formState.errors.initialTotalCompensation?.message as string}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onValueChange={(values) => {
                    field.onChange(values.value ? parseInt(values.value, 10) : null);
                    form.trigger("initialTotalCompensation");
                  }}
                  leadingIcon="attach_money"
                  label="Initial Total Compensation"
                  placeholder="Initial Total Compensation"
                />
              )}
            />
          </div>
        )}
        <div className="col-span-full md:col-span-2">
          <p className="text-title-medium mb-2">Base Pay Type</p>
          <Controller
            control={form.control}
            name="basePayType"
            render={({ field }) => (
              <div className="flex flex-col" data-testid="compensation-scale-radio-group">
                {COMPENSATION_SCALES.map((option, i) => (
                  <div className={"py-3"} key={i}>
                    <Radio
                      name={`basePayType-${option.value}`}
                      label={option.label}
                      checked={field.value === option.value}
                      onChange={() => {
                        field.onChange(option.value);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          />
        </div>
        <div className="col-span-full md:col-span-2">
          {form.watch("basePayType") !== "Salary" && (
            <Controller
              control={form.control}
              name="payRate"
              render={({ field }) => (
                <NumberFormatBase
                  value={form.watch("basePayType") !== "Salary" ? field.value : null}
                  name="payRate"
                  className="w-full"
                  customInput={TextField}
                  error={!!form.formState.errors.employerMatch401kPercent}
                  errorText={form.formState.errors.employerMatch401kPercent?.message as string}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  format={currencyFormatterNoDecimals}
                  onValueChange={(values) => {
                    field.onChange(values.value ? parseInt(values.value, 10) : null);
                    form.trigger("payRate");
                  }}
                  leadingIcon="attach_money"
                  label={rateLabel}
                  placeholder={rateLabel}
                />
              )}
            />
          )}
        </div>
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="totalMonthsWorkedAnnual"
          rules={{
            min: {
              value: 0,
              message: "Number of Months Worked Annually must be greater than or equal to 0"
            },
            max: {
              value: 12,
              message: "Number of Months Worked Annually must be less than or equal to 12"
            }
          }}
          render={({ field }) => (
            <NumberFormatBase
              disabled={false}
              value={field.value}
              name="totalMonthsWorkedAnnual"
              className="w-full"
              customInput={TextField}
              error={!!form.formState.errors.totalMonthsWorkedAnnual}
              errorText={form.formState.errors.totalMonthsWorkedAnnual?.message as string}
              onFocus={(e) => {
                e.target.select();
              }}
              onValueChange={(values) => {
                field.onChange(values.value ? parseInt(values.value, 10) : null);
                form.trigger("totalMonthsWorkedAnnual");
              }}
              label="Number of Months Worked Annually"
              placeholder="Number of Months Worked Annually"
            />
          )}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="col-span-full md:col-span-2">
          <Controller
            control={form.control}
            name="basePay"
            render={({ field }) => (
              <NumberFormatBase
                disabled={false}
                value={field.value}
                format={currencyFormatterNoDecimals}
                name="basePay"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.basePay}
                errorText={form.formState.errors.basePay?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("basePay");
                }}
                leadingIcon="attach_money"
                label="Base Pay (Annual)"
                placeholder="Base Pay"
              />
            )}
          />
        </div>
        <div className="col-span-full md:col-span-2">
          <Controller
            control={form.control}
            name="bonusIncome"
            render={({ field }) => (
              <NumberFormatBase
                disabled={false}
                value={field.value}
                name="bonusIncome"
                format={currencyFormatterNoDecimals}
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.bonusIncome}
                errorText={form.formState.errors.bonusIncome?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("bonusIncome");
                }}
                leadingIcon="attach_money"
                label="Bonus Income"
                placeholder="Bonus Income"
              />
            )}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="col-span-full md:col-span-2">
          <Controller
            control={form.control}
            name="lessonIncome"
            render={({ field }) => (
              <NumberFormatBase
                disabled={false}
                value={field.value}
                name="lessonIncome"
                format={currencyFormatterNoDecimals}
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.lessonIncome}
                errorText={form.formState.errors.lessonIncome?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("lessonIncome");
                }}
                leadingIcon="attach_money"
                label="Lesson Income"
                placeholder="Lesson Income"
              />
            )}
          />
        </div>
        <div className="col-span-full md:col-span-2">
          <Controller
            control={form.control}
            name="otherIncome"
            render={({ field }) => (
              <NumberFormatBase
                disabled={false}
                value={field.value}
                name="otherIncome"
                format={currencyFormatterNoDecimals}
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.otherIncome}
                errorText={form.formState.errors.otherIncome?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("otherIncome");
                }}
                leadingIcon="attach_money"
                label="Other Income"
                placeholder="Other Income"
              />
            )}
          />
        </div>
      </div>
      <div className="py-3">
        <Divider />
      </div>
      <div className="flex justify-between gap-4">
        <p className="text-title-medium">Total Compensation</p>
        <p className="text-title-medium">
          ${" "}
          {currencyFormatterNoDecimals(
            (
              Number(form.getValues("basePay") || 0) +
              Number(form.getValues("bonusIncome") || 0) +
              Number(form.getValues("lessonIncome") || 0) +
              Number(form.getValues("otherIncome") || 0)
            ).toString()
          )}
        </p>
      </div>
    </div>
  );
}
